import { differenceInCalendarDays, parseISO, startOfToday } from 'date-fns';
import { z } from 'zod';

import type { CampaignRestrictions } from '../../types';

export interface SchemaRestrictions {
  minLeadDays: number;
  minClippingBudget: number;
  minInfluencerBudget: number;
}

export interface ResolvedSchemaRestrictions extends SchemaRestrictions {
  clippingVendor: string;
  influencerVendor: string;
}

const DEFAULT_RESTRICTIONS: SchemaRestrictions = {
  minLeadDays: 3,
  minClippingBudget: 3500,
  minInfluencerBudget: 4500,
};

export const DEFAULT_CLIPPING_VENDOR = 'Clip Tech';
export const DEFAULT_INFLUENCER_VENDOR = 'Producer Labs';

const AGENCY_DISPLAY_NAMES: Record<string, string> = {
  CLIP_TECH: DEFAULT_CLIPPING_VENDOR,
  CLIPPING: DEFAULT_CLIPPING_VENDOR,
  PRODUCER_LABS: DEFAULT_INFLUENCER_VENDOR,
  INFLUENCER: DEFAULT_INFLUENCER_VENDOR,
};

function vendorFromAgencyChannel(
  agencyChannel: string[],
  fallback: string,
): string {
  for (const channel of agencyChannel) {
    const name = AGENCY_DISPLAY_NAMES[channel];

    if (name) {
      return name;
    }
  }

  return fallback;
}

/** Default lead-time floor (days); kept in sync with `createCampaignSchema` fallback. */
export const DEFAULT_CREATE_CAMPAIGN_MIN_LEAD_DAYS =
  DEFAULT_RESTRICTIONS.minLeadDays;

export const DEFAULT_CREATE_CAMPAIGN_MIN_CLIPPING_BUDGET =
  DEFAULT_RESTRICTIONS.minClippingBudget;

export const DEFAULT_CREATE_CAMPAIGN_MIN_INFLUENCER_BUDGET =
  DEFAULT_RESTRICTIONS.minInfluencerBudget;

export function restrictionsToSchema(
  restrictions: CampaignRestrictions,
): SchemaRestrictions {
  return {
    minLeadDays: restrictions.clipping.min_lead_time_in_days,
    minClippingBudget: restrictions.clipping.min_budget / 100,
    minInfluencerBudget: restrictions.influencer.min_budget / 100,
  };
}

/** API restrictions when available; otherwise schema/placeholder defaults. */
export function resolveSchemaRestrictions(
  restrictions?: CampaignRestrictions,
): ResolvedSchemaRestrictions {
  if (!restrictions) {
    return {
      ...DEFAULT_RESTRICTIONS,
      clippingVendor: DEFAULT_CLIPPING_VENDOR,
      influencerVendor: DEFAULT_INFLUENCER_VENDOR,
    };
  }

  return {
    ...restrictionsToSchema(restrictions),
    clippingVendor: vendorFromAgencyChannel(
      restrictions.clipping.agency_channel,
      DEFAULT_CLIPPING_VENDOR,
    ),
    influencerVendor: vendorFromAgencyChannel(
      restrictions.influencer.agency_channel,
      DEFAULT_INFLUENCER_VENDOR,
    ),
  };
}

export function formatMinimumSpend(dollars: number): string {
  return `$${dollars.toLocaleString('en-US')}`;
}

const baseSchema = z.object({
  campaignStartDate: z
    .string()
    .min(1, 'Start date is required')
    .pipe(z.iso.date('Must be a valid date (YYYY-MM-DD)')),
  advertiser: z.string().min(1, 'Advertiser is required'),
  artist: z
    .string()
    .transform((v) => v.trim())
    .pipe(z.string().min(1, 'Artist is required')),
  track: z
    .string()
    .transform((v) => v.trim())
    .pipe(z.string().min(1, 'Track is required')),
  isrc: z
    .string()
    .transform((v) => v.trim().toUpperCase())
    .pipe(
      z
        .string()
        .min(1, 'ISRC is required')
        .regex(/^[A-Z]{2}[A-Z0-9]{3}\d{7}$/, 'Must be a valid ISRC format'),
    ),
  spotifyUrl: z
    .string()
    .min(1, 'Spotify URL is required')
    .pipe(z.url('Must be a valid URL')),
  tiktokLink: z
    .string()
    .transform((v) => v.trim())
    .pipe(
      z
        .string()
        .min(1, 'TikTok link is required')
        .pipe(z.url('Must be a valid URL')),
    ),
  instagramLink: z
    .string()
    .transform((v) => v.trim())
    .pipe(
      z
        .string()
        .min(1, 'Instagram link is required')
        .pipe(z.url('Must be a valid URL')),
    ),
  clippingEnabled: z.boolean(),
  clippingBudget: z.number().optional(),
  clippingPurchaseOrder: z.string().optional(),
  influencerEnabled: z.boolean(),
  influencerBudget: z.number().optional(),
  influencerPurchaseOrder: z.string().optional(),
});

export function createCampaignSchema(
  restrictions: SchemaRestrictions = DEFAULT_RESTRICTIONS,
) {
  const { minLeadDays, minClippingBudget, minInfluencerBudget } = restrictions;

  return baseSchema.superRefine((data, ctx) => {
    if (data.clippingEnabled) {
      if (!data.clippingBudget || data.clippingBudget < minClippingBudget) {
        ctx.addIssue({
          code: 'custom',
          message: `Minimum spend is $${minClippingBudget.toLocaleString()}`,
          path: ['clippingBudget'],
        });
      }

      if (!data.clippingPurchaseOrder?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'Purchase order is required',
          path: ['clippingPurchaseOrder'],
        });
      }
    }

    if (data.influencerEnabled) {
      if (
        !data.influencerBudget ||
        data.influencerBudget < minInfluencerBudget
      ) {
        ctx.addIssue({
          code: 'custom',
          message: `Minimum spend is $${minInfluencerBudget.toLocaleString()}`,
          path: ['influencerBudget'],
        });
      }

      if (!data.influencerPurchaseOrder?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'Purchase order is required',
          path: ['influencerPurchaseOrder'],
        });
      }
    }

    const hasChannel = data.clippingEnabled || data.influencerEnabled;

    if (!hasChannel) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one campaign channel must be enabled',
        path: ['clippingEnabled'],
      });

      return;
    }

    if (!data.campaignStartDate) {
      return;
    }

    const today = startOfToday();
    const start = parseISO(data.campaignStartDate);

    const leadDays = differenceInCalendarDays(start, today);

    if (leadDays < minLeadDays) {
      ctx.addIssue({
        code: 'custom',
        message: `Start date must be at least ${minLeadDays} days from today`,
        path: ['campaignStartDate'],
      });
    }
  });
}

export type CreateCampaignFormValues = z.input<
  ReturnType<typeof createCampaignSchema>
>;
